import { useLayoutEffect, useRef, useState } from "react";
import { Focusable } from "react-aria-components";
import { cn } from "@core/utils";
import { Avatar, type AvatarSize } from "./Avatar";
import { Tooltip } from "./Tooltip";

interface PersonIdentityProps {
  /** Full name: shown, and the source of the avatar's initials. */
  name: string;
  /** Shown under the name when there is one. */
  email?: string;
  size?: AvatarSize;
  className?: string;
}

/** Text that is clipped with an ellipsis, with a tooltip carrying the whole text when it is. */
function ClippedText({ text, className }: { text: string; className: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const [isClipped, setClipped] = useState(false);

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;
    const measure = () => {
      setClipped(element.scrollWidth > element.clientWidth);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, [text]);

  // The tooltip only exists while the text is clipped: nothing to say otherwise, and no extra stop
  // for the keyboard.
  const line = (
    <span ref={ref} className={cn("block truncate", className)}>
      {text}
    </span>
  );

  return isClipped ? (
    <Tooltip content={text} delay={0}>
      {/* `Focusable` makes the clipped text reachable by keyboard, so the tooltip opens on focus. */}
      <Focusable>
        <span className="block min-w-0 rounded-xs outline-none focus-visible:ring-2 focus-visible:ring-focus-ring">
          {line}
        </span>
      </Focusable>
    </Tooltip>
  ) : (
    <span className="block min-w-0">{line}</span>
  );
}

/**
 * A person wherever one is listed or chosen: the initials avatar, the full name and, under it, the
 * e-mail. A name or address that does not fit is clipped with an ellipsis and a tooltip gives the
 * whole text on hover or focus. Use it for every member in a list, a picker or a menu, so people
 * look the same across the app.
 */
export function PersonIdentity({
  name,
  email,
  size = "sm",
  className,
}: PersonIdentityProps) {
  return (
    <span className={cn("flex min-w-0 items-center gap-sm", className)}>
      <Avatar name={name} size={size} />
      <span className="flex min-w-0 flex-1 flex-col">
        <ClippedText
          text={name}
          className="text-sm-medium text-(--text-primary)"
        />
        {email ? (
          <ClippedText
            text={email}
            className="text-xs-regular text-(--text-secondary)"
          />
        ) : null}
      </span>
    </span>
  );
}

export type { PersonIdentityProps };
